import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IValidacaoCRLV } from "../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface FormValues {
  id_estado: number | string;
  ano_exercicio: number | string;
  final_placa: number | string;
  validade_exercicio: string;
  numero_portaria: string;
  ativo: boolean;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IValidacaoCRLV;
  onConfirm: () => void;
  onClose: () => void;
  states: any[];
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleSubmit = useCallback(async (values: FormValues, row?: IValidacaoCRLV) => {
    try {
      setLoading(true);
      
      const id = row?.id_configuracao_crlv || row?.id_validacao_crlv || row?.id;

      const body = {
        id_configuracao_crlv: id,
        id_validacao_crlv: id,
        id: id,
        id_estado: Number(values.id_estado),
        ano_exercicio: Number(values.ano_exercicio),
        final_placa: Number(values.final_placa),
        validade_exercicio: values.validade_exercicio,
        numero_portaria: values.numero_portaria,
        status: 1,
        id_usuario_historico: user?.id,
      };

      if (props.isEdit) {
        await api.post("/editar/configuracaoCRLV", body);
      } else {
        await api.post("/cadastrar/configuracaoCRLV", body);
      }

      setLoading(false);
      props.onConfirm();
    } catch {
      setLoading(false);
    }
  }, [props.isEdit, props.onConfirm, user]);

  const onLoadFormValues = useCallback((row: IValidacaoCRLV) => {
    if (row) {
      formik.setFieldValue("id_estado", row.id_estado);
      formik.setFieldValue("ano_exercicio", row.ano_exercicio);
      formik.setFieldValue("final_placa", row.final_placa);
      formik.setFieldValue("validade_exercicio", row.validade_exercicio ? row.validade_exercicio.split('T')[0] : '');
      formik.setFieldValue("numero_portaria", row.numero_portaria);
      formik.setFieldValue("ativo", row.ativo);
    }
  }, []);

  const initialValues: FormValues = {
    id_estado: "",
    ano_exercicio: "",
    final_placa: "",
    validade_exercicio: "",
    numero_portaria: "",
    ativo: true,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) => handleSubmit(values, props.selectedRow),
  });

  useEffect(() => {
    if ((props.isView || props.isEdit) && props.selectedRow) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.isView, props.isEdit, props.selectedRow, onLoadFormValues]);

  return (
    <>
      <Loading loading={loading} skeletonFields={5} skeletonColumns={2} />
      <div className="grid grid-cols-2 gap-3 mb-4 p-5">
        <div>
            <SelectCustom
                id="id_estado"
                title="Estado"
                placeholder="Selecione o estado"
                onChange={(e: any) => formik.setFieldValue("id_estado", e.value)}
                value={props.states.find((s) => s.id_estado === formik.values.id_estado) ? { value: formik.values.id_estado, label: props.states.find((s) => s.id_estado === formik.values.id_estado).nome } : null}
                data={props.states.map((s) => ({ value: s.id_estado, label: s.nome }))}
                error={formik.errors.id_estado}
                touched={formik.touched.id_estado}
                disabled={props.isView}
            />
        </div>
        <div>
            <InputCustom
                id="ano_exercicio"
                title="Ano de Exercício"
                placeholder="Informe o ano"
                onChange={formik.handleChange("ano_exercicio")}
                touched={formik.touched.ano_exercicio}
                error={formik.errors.ano_exercicio}
                value={formik.values.ano_exercicio}
                disabled={props.isView}
                type="number"
            />
        </div>
        <div>
            <InputCustom
                id="final_placa"
                title="Final da Placa"
                placeholder="Informe o final da placa"
                onChange={formik.handleChange("final_placa")}
                touched={formik.touched.final_placa}
                error={formik.errors.final_placa}
                value={formik.values.final_placa}
                disabled={props.isView}
                type="number"
            />
        </div>
        <div>
            <InputCustom
                id="validade_exercicio"
                title="Validade do Exercício"
                placeholder="Informe a validade"
                onChange={formik.handleChange("validade_exercicio")}
                touched={formik.touched.validade_exercicio}
                error={formik.errors.validade_exercicio}
                value={formik.values.validade_exercicio}
                disabled={props.isView}
                type="date"
            />
        </div>
        <div>
            <InputCustom
                id="numero_portaria"
                title="Número da Portaria"
                placeholder="Informe o número da portaria"
                onChange={formik.handleChange("numero_portaria")}
                touched={formik.touched.numero_portaria}
                error={formik.errors.numero_portaria}
                value={formik.values.numero_portaria}
                disabled={props.isView}
            />
        </div>
      </div>
      <div className="w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        {!props.isView && (
            <button
            type="button"
            className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
            onClick={() => formik.handleSubmit()}
            id="btnSave"
            >
            Salvar
            </button>
        )}
      </div>
    </>
  );
};

export default Form;
